export type PwaInstallMilestone = "logged-in";

export type PwaInstallMilestoneRecord = {
  at: number;
  milestone: PwaInstallMilestone;
};

export const PWA_INSTALL_MILESTONE_EVENT = "pwa-install-milestone";
export const PWA_INSTALL_MILESTONE_KEY = "pwa-install-milestone";

type NavigatorWithPwaCapabilities = Navigator & {
  clearAppBadge?: () => Promise<void>;
  setAppBadge?: (contents?: number) => Promise<void>;
  standalone?: boolean;
};

export type PwaNotificationPermission = NotificationPermission | "unsupported";

export function isPwaStandalone() {
  if (typeof window === "undefined") return false;

  return (
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: minimal-ui)").matches ||
    Boolean((navigator as NavigatorWithPwaCapabilities).standalone)
  );
}

export function getPwaDisplayMode() {
  if (typeof window === "undefined") return "browser";

  if (window.matchMedia("(display-mode: fullscreen)").matches) {
    return "fullscreen";
  }

  if (window.matchMedia("(display-mode: standalone)").matches) {
    return "standalone";
  }

  if (window.matchMedia("(display-mode: minimal-ui)").matches) {
    return "minimal-ui";
  }

  if ((navigator as NavigatorWithPwaCapabilities).standalone) {
    return "standalone";
  }

  return "browser";
}

export function supportsAppBadging() {
  if (typeof navigator === "undefined") return false;

  return (
    typeof (navigator as NavigatorWithPwaCapabilities).setAppBadge ===
    "function"
  );
}

export function getPwaNotificationPermission(): PwaNotificationPermission {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }

  return window.Notification.permission;
}

export async function requestPwaNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported" as const;
  }

  return window.Notification.requestPermission();
}

export async function syncAppBadge(count: number) {
  if (!supportsAppBadging()) {
    return "unsupported" as const;
  }

  const safeCount = Math.max(0, Math.min(Math.floor(count), 99));
  const navigatorWithBadge = navigator as NavigatorWithPwaCapabilities;

  try {
    if (safeCount > 0) {
      await navigatorWithBadge.setAppBadge?.(safeCount);
      return "set" as const;
    }

    if (navigatorWithBadge.clearAppBadge) {
      await navigatorWithBadge.clearAppBadge();
    } else {
      await navigatorWithBadge.setAppBadge?.(0);
    }

    return "cleared" as const;
  } catch {
    return "failed" as const;
  }
}

export function getPwaInstallMilestone():
  | PwaInstallMilestoneRecord
  | undefined {
  if (typeof window === "undefined") return undefined;

  try {
    const value = window.localStorage.getItem(PWA_INSTALL_MILESTONE_KEY);
    if (!value) return undefined;

    const parsed = JSON.parse(value) as Partial<PwaInstallMilestoneRecord>;
    if (!parsed.at || !parsed.milestone) return undefined;

    return {
      at: parsed.at,
      milestone: parsed.milestone,
    };
  } catch {
    return undefined;
  }
}

export function markPwaInstallMilestone(milestone: PwaInstallMilestone) {
  if (typeof window === "undefined") return;

  const record: PwaInstallMilestoneRecord = {
    at: Date.now(),
    milestone,
  };

  window.localStorage.setItem(
    PWA_INSTALL_MILESTONE_KEY,
    JSON.stringify(record),
  );
  window.dispatchEvent(
    new CustomEvent(PWA_INSTALL_MILESTONE_EVENT, { detail: record }),
  );
}
